import React, { Fragment } from 'react';
import { withLocalize } from 'react-localize-redux';
import { compose } from 'recompose';
import { isEmpty, get } from 'lodash';
import axios from 'axios';
import { GET_TEMP_BUCKET_PREVIEW_URL, GET_LARGE_FILE_PREVIEW_URL, PUBLIC_BUCKET_CDN, POST_UPLOAD_ACTION } from '../../network/api';
import { handleResponseCode } from '../../actions';
import FileUploader from '../../uploader/Modal';
import ErrorMessage from './errorMessage';
import { hlog, HHAlert } from '../../common/util';
import { ALERT_TYPE } from '../../common/constant';


const defaultTrigger = props => <button type='button'>{props.translate('upload.file')}</button>;

const getOptions = type => {
  switch (type) {
    case 'document':
      return { accept: '.pdf, .doc, .docx, .ppt, .pptx' };
    case 'image_37x9':
      return { accept: 'image/*', cropRatios: ['37:9'] };
    case 'image_12x8':
      return { accept: 'image/*', cropRatios: ['12:8'] };
    case 'image_16x9':
      return { accept: 'image/*', cropRatios: ['16:9'] };
    case 'image_1x1':
      return { accept: 'image/*', cropRatios: ['1:1'] };
    case 'image':
      return { accept: 'image/*', cropRatios: ['free', '16:9', '1:1'] };
    case 'compression':
      return { accept: '.zip' };
      // return { accept: '.rar, .zip, .7z' };
    case 'compression + document':
      return { accept: '.zip, .pdf, .ppt, .pptx, .doc, .docx' };
      // return { accept: '.rar, .zip, .7z, .pdf, .ppt, .pptx, .html, .md' };
    case 'pdf':
      return { accept: '.pdf' };
    default:
      return {};
  }
};

const largeFileTypes = ['user_project_content', 'event_submission_content', 'event_resource_content'];

const HHFileUploader = ({
  field,
  form: {
    touched, errors, setFieldValue, values
  },
  otherProps: {
    trigger, type, onUpload, fileType, slug, userToken, ele, contentId
  }
}) => {
  const hasError = !!(touched[field.name] && errors[field.name]);

  const getPreviewLink = async (endpoint, parameter) => {
    try {
      const res = await axios.post(endpoint, parameter);
      const { code, response } = res.data;
      if (response && code === 200) {
        return response;
      }
      handleResponseCode(code);
      return {};
    } catch (err) {
      HHAlert({ id: 'alert.error.actionFailed' }, ALERT_TYPE.ERROR);
      hlog(err);
      return {};
    }
  };

  const postUpload = async (parameter) => {
    try {
      const res = await axios.post(POST_UPLOAD_ACTION, parameter);
      const { code, response } = res.data;
      if (response && code === 200) {
        return response;
      }
      handleResponseCode(code);
      return {};
    } catch (err) {
      HHAlert({ id: 'alert.error.uploadFailed' }, ALERT_TYPE.ERROR);
      hlog(err);
      return {};
    }
  };

  const isAcceptFileType = (fileType) => fileType === 'event_banner' || 'event_cover' || 'event_submission_thumbnail' || 'event_resource_thumbnail' || 'user_project_thumbnail' || 'event_streaming_thumbnail' || 'event_submission_content' || 'event_resource_content' || 'screening_application_form_upload' || 'user_project_content';

  const setFileTypeFieldValue = fileType => {
    const type = {
      event_banner: 'bannerBgTempUrl',
      event_cover: 'coverImageTempUrl',
      event_submission_thumbnail: 'previewTempUrl',
      event_resource_thumbnail: 'previewTempUrl',
      user_project_thumbnail: 'previewTempUrl',
      event_streaming_thumbnail: 'coverTempUrl',
      event_submission_content: 'fileTempUrl',
      event_resource_content: 'fileTempUrl',
      screening_application_form_upload: 'fileTempUrl',
      user_project_content: 'fileTempUrl'
    };

    return type[fileType];
  };

  const handleChange = async key => {
    if (fileType === 'event_sponsor_images') {
      const parameter = {
        token: userToken,
        files: [
          {
            objectKey: key,
            fileType,
            objectPathTemplateData: {
              eventSlug: slug
            }
          }
        ],
        eventSlug: slug
      };
      const res = await postUpload(parameter);
      if (isEmpty(res)) return;
      const newObjectKey = get(res, ['keysToAttributes', `${key}`, 'newObjectKey']);

      const addSource = () => new Promise((resolve, reject) => {
        setTimeout(() => {
          const source = `${PUBLIC_BUCKET_CDN}${newObjectKey}`;
          const arr = values[field.name];
          if (ele < values.sponsor.length) {
            arr.splice(ele, 1, `${source}`);
          } else {
            arr.push(`${source}`);
          }
          setFieldValue(field.name, arr);
          resolve(true);
        }, [1000]);
      });

      const isAdded = await addSource();
      if (isAdded) return;
    }

    let parameter;
    if (fileType === 'event_resource_content' || fileType === 'event_submission_content') {
      parameter = {
        token: userToken,
        fileKeys: [key],
        fileType,
        eventSlug: slug
      };
    } else if (fileType === 'user_project_content') {
      parameter = {
        token: userToken,
        fileKeys: [key],
        fileType
      };
    } else {
      parameter = {
        token: userToken,
        objectKey: key,
        fileType
      };
    }

    let previewLinkRes;
    if (largeFileTypes.includes(fileType)) {
      previewLinkRes = await getPreviewLink(GET_LARGE_FILE_PREVIEW_URL, parameter);
      if (isEmpty(previewLinkRes)) return;
      const { keysToLink } = previewLinkRes;
      const url = keysToLink[key];
      const isFileTypeOk = isAcceptFileType(fileType);
      if (!isFileTypeOk) return;
      const fieldVal = setFileTypeFieldValue(fileType);
      setFieldValue(fieldVal, url);
    } else {
      previewLinkRes = await getPreviewLink(GET_TEMP_BUCKET_PREVIEW_URL, parameter);
      if (isEmpty(previewLinkRes)) return;
      const { url } = previewLinkRes;
      const isFileTypeOk = isAcceptFileType(fileType);
      if (!isFileTypeOk) return;
      const fieldVal = setFileTypeFieldValue(fileType);
      setFieldValue(fieldVal, url);
    }
    setFieldValue(field.name, `${key}`);

    if (typeof onUpload === 'function') onUpload(key);
  };

  return (
    <Fragment>
      <FileUploader
        // onChange={res => {
        //   if (res.data.code && res.data.code !== 200) {
        //     handleResponseCode(res.data.code);
        //   } else {
        //     setFieldValue(field.name, res.data.Location);
        //     if (typeof onUpload === 'function') onUpload(res);
        //   }
        // }}
        onChange={handleChange}

        trigger={trigger || defaultTrigger}
        {...getOptions(type)}
        fileType={fileType}
        slug={slug || undefined}
        contentId={contentId || undefined}

      />
      {hasError && (<ErrorMessage error={errors[field.name]} />)}
    </Fragment>
  );
};

export default compose(withLocalize)(HHFileUploader);




// WEBPACK FOOTER //
// ./src/components/forms/fields/hhFileUploader.js